<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { ref, computed, inject } from "vue"
import { useI18n } from "vue-i18n"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import type { Services } from "@services"
import type { Building } from "@types"
import type {
    UsageProfile,
    UsageProfileWeekDay,
    UsageProfilePublicHoliday,
    UsageProfileSchoolHoliday,
} from "@modules/usage-profile/types"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            apply: "Enregistrer",
            modification_error: "Erreur lors de la modification du profil.",
            creation_error: "Erreur lors de la création du profil.",
            creation_error_restricted_admin_company:
                "Seuls les administrateurs peuvent créer des profils.",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** Bâtiment associé (depuis page bâtiment) */
        buildingId?: string | null
        /** Identifiant de l'entreprise */
        companyId: string
        /** Profil en cours d'édition (null = création) */
        profile?: UsageProfile | null
    }>(),
    {
        buildingId: null,
        profile: null,
    }
)

const emit = defineEmits<{
    "click:create": [profile: UsageProfile]
    "click:modify": [profile: UsageProfile]
}>()

// ================================
// SERVICES
// ================================
const services = inject<Services>("services")!

// ================================
// STATE
// ================================
/** Jours de la semaine par défaut (ouverts H24 pour création) */
const WEEK_DAYS: UsageProfileWeekDay[] = [
    { jour: "lundi", ouvert: true },
    { jour: "mardi", ouvert: true },
    { jour: "mercredi", ouvert: true },
    { jour: "jeudi", ouvert: true },
    { jour: "vendredi", ouvert: true },
    { jour: "samedi", ouvert: true },
    { jour: "dimanche", ouvert: true },
]

/** Profil de la semaine (horaires par jour) */
const week = ref<UsageProfileWeekDay[]>(
    props.profile?.profil_semaine?.length
        ? JSON.parse(JSON.stringify(props.profile.profil_semaine))
        : WEEK_DAYS
)
/** Nom du profil */
const name = ref(props.profile?.name ?? "Profil sans titre")
/** Bâtiments associés au profil (mis à jour par UsageProfileEditBuilding) */
const buildingsProfil = ref<(Building | { _id: string })[]>(
    (props.profile?.buildings ?? []).map((b) => ({ _id: b._id }))
)
/** Config vacances scolaires */
const schoolHoliday = ref<UsageProfileSchoolHoliday>(
    props.profile?.fermeture_vacances ?? { ferme: true, zone: "A" }
)
/** Config jours fériés */
const publicHoliday = ref<UsageProfilePublicHoliday>(
    props.profile?.fermeture_jourferie ?? { ferme: true }
)
/** Message d'erreur affiché */
const errorMessage = ref("")

// ================================
// FETCH WRAPPER (enregistrement)
// ================================
/** Wrapper pour l'enregistrement du profil (création ou modification) */
const { fetch: applyProfileWrapped, isFetching: loadingApply } =
    useFetchWrapper(
        async () => {
            errorMessage.value = ""
            const data: Record<string, unknown> = {
                name: name.value,
                profil_semaine: week.value,
                fermeture_vacances: schoolHoliday.value,
                fermeture_jourferie: publicHoliday.value,
            }

            if (props.buildingId) {
                data._id_buildings = [props.buildingId]
            } else {
                data._id_buildings = buildingsProfil.value.map((b) =>
                    typeof b === "string" ? b : b._id
                )
            }

            const initialBuildingIds = (props.profile?.buildings ?? []).map(
                (b) => b._id
            )
            const currentIds = new Set(
                buildingsProfil.value.map((b) =>
                    typeof b === "string" ? b : b._id
                )
            )
            const removedIds = initialBuildingIds.filter(
                (id) => !currentIds.has(id)
            )

            if (removedIds.length > 0 && props.profile?._id) {
                await services.$deleteUsageProfileBuilding({
                    profileId: props.profile._id,
                    buildingIds: removedIds,
                })
            }

            if (props.profile?._id) {
                let profileWithBuildings
                profileWithBuildings = await services.$updateUsageProfile({
                    profileId: props.profile._id,
                    profile: data,
                })
                const buildingIds = buildingsProfil.value.map((b) =>
                    typeof b === "string" ? b : b._id
                )
                if (buildingIds.length > 0) {
                    profileWithBuildings =
                        await services.$addUsageProfileToBuilding({
                            profileId: props.profile._id,
                            buildingIds: buildingIds,
                        })
                }

                emit("click:modify", profileWithBuildings)
            } else {
                const profileCreated = await services.$createUsageProfile({
                    profile: data,
                })
                emit("click:create", profileCreated)
            }
        },
        (err: Error & { code?: number }) => {
            if (err?.code === 403) {
                errorMessage.value = t(
                    "creation_error_restricted_admin_company"
                )
            } else if (props.profile?._id) {
                errorMessage.value = t("modification_error")
            } else {
                errorMessage.value = t("creation_error")
            }
        }
    )

// ================================
// COMPUTED
// ================================
/** Formulaire invalide si au moins un jour sans horaires */
const getDisabled = computed(() => {
    return week.value.some(
        (day) =>
            !day.ouvert &&
            !day.ferme &&
            (!day.horaires_ouverture || day.horaires_ouverture.length === 0)
    )
})

// ================================
// INTERACTIONS
// ================================
/** Met à jour le nom depuis le formulaire général */
function onInputGeneral(payload: { name: string }): void {
    name.value = payload.name
}

/** Met à jour les bâtiments associés (émit par UsageProfileEditBuilding) */
function onUpdateBuildings(payload: (Building | { _id: string })[]): void {
    buildingsProfil.value = payload
}

/** Met à jour la semaine depuis le formulaire horaires */
function onInputWeek(payload: { week: UsageProfileWeekDay[] }): void {
    week.value = payload.week
}

/** Met à jour la config vacances scolaires */
function onInputSchoolHoliday(value: UsageProfileSchoolHoliday): void {
    schoolHoliday.value = value
}

/** Met à jour la config jours fériés */
function onInputPublicHoliday(value: UsageProfilePublicHoliday): void {
    publicHoliday.value = value
}

/** Enregistre le profil (création ou modification) */
function onApply(): void {
    applyProfileWrapped()
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <div class="usage-profile-edit flex flex-col gap-8 max-w-3xl mx-auto">
        <UsageProfileEditGeneral
            id="edit-general"
            :name="name"
            :display-building="!buildingId"
            :company-id="companyId"
            :buildings-profil="buildingsProfil"
            @input="onInputGeneral"
            @update:buildings="onUpdateBuildings"
        />
        <UsageProfileEditWeek
            id="edit-week"
            :value="week"
            @input="onInputWeek"
        />
        <UsageProfileEditException
            id="edit-exception"
            :school-holiday="schoolHoliday"
            :public-holiday="publicHoliday"
            @input:schoolholiday="onInputSchoolHoliday"
            @input:public-holiday="onInputPublicHoliday"
        />
        <BaseAppMessage
            v-if="errorMessage"
            severity="error"
            :message="errorMessage"
        />
        <BaseButton
            :label="t('apply')"
            :disabled="getDisabled || loadingApply"
            :loading="loadingApply"
            class="self-center"
            @click="onApply"
        />
    </div>
</template>
